import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  pairContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  pairCard: {
    border: '1px solid #f3f4f6',
    borderRadius: '6px',
    padding: '16px',
  },
  pairLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  pairInterpretation: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '10px',
    fontStyle: 'italic',
  },
  summarySection: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  summaryTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  summaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#374151',
  },
  summaryDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  summaryValue: {
    fontWeight: 600,
    color: '#1f2937',
    marginLeft: '4px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

const COLORS = {
  leading: '#2563eb',
  lagging: '#ea580c',
  synchronous: '#9ca3af',
  peakHighlight: '#dc2626',
  barDefault: '#d1d5db',
};

const MARGIN = { top: 10, right: 20, bottom: 26, left: 40 };

function getLagColor(lag) {
  if (lag < 0) return COLORS.leading;
  if (lag > 0) return COLORS.lagging;
  return COLORS.synchronous;
}

function getLagLabel(lag) {
  if (lag < 0) return 'Leading';
  if (lag > 0) return 'Lagging';
  return 'Synchronous';
}

function formatPairLabel(from, to) {
  return `${from} \u2192 ${to}`;
}

export default function LeadLagIndicatorChart({ data }) {
  const svgRefs = useRef({});
  const [tooltip, setTooltip] = useState(null);

  const pairs = useMemo(() => {
    if (!data || !data.pairs || data.pairs.length === 0) return [];
    return data.pairs;
  }, [data]);

  const significantPairs = useMemo(() => {
    return pairs.filter((p) => Math.abs(p.peakCorrelation) >= 0.3 && p.peakLag !== 0);
  }, [pairs]);

  // Draw a cross-correlogram for each pair
  useEffect(() => {
    if (pairs.length === 0) return;

    pairs.forEach((pair, idx) => {
      const svgEl = svgRefs.current[idx];
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const viewW = 500;
      const viewH = 140;
      svg.attr('viewBox', `0 0 ${viewW} ${viewH}`);

      const innerW = viewW - MARGIN.left - MARGIN.right;
      const innerH = viewH - MARGIN.top - MARGIN.bottom;

      const g = svg.append('g')
        .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

      const lags = pair.lags || [];
      const correlations = pair.correlations || [];
      if (lags.length === 0) return;

      // X scale — one band per lag
      const x = d3.scaleBand()
        .domain(lags.map(String))
        .range([0, innerW])
        .padding(0.2);

      // Y scale — correlation range
      const maxCorr = d3.max(correlations.map(Math.abs)) || 1;
      const yDomainMax = Math.min(Math.max(maxCorr * 1.15, 0.2), 1.05);
      const y = d3.scaleLinear()
        .domain([0, yDomainMax])
        .nice()
        .range([innerH, 0]);

      // Horizontal grid lines
      g.selectAll('.grid-h')
        .data(y.ticks(4))
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Zero line at bottom
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      // Vertical line at lag 0
      const zeroLagKey = '0';
      if (lags.map(String).includes(zeroLagKey)) {
        const zeroX = x(zeroLagKey) + x.bandwidth() / 2;
        g.append('line')
          .attr('x1', zeroX)
          .attr('x2', zeroX)
          .attr('y1', 0)
          .attr('y2', innerH)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }

      // X axis
      g.append('g')
        .attr('transform', `translate(0, ${innerH})`)
        .call(
          d3.axisBottom(x)
            .tickSize(0)
            .tickFormat((d) => d)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280')
        .attr('dy', '0.8em');

      // X axis label
      g.append('text')
        .attr('x', innerW / 2)
        .attr('y', innerH + 22)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', '#9ca3af')
        .text('Lag period');

      // Y axis
      g.append('g')
        .call(
          d3.axisLeft(y)
            .ticks(4)
            .tickSize(0)
            .tickFormat((d) => d.toFixed(1))
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280');

      // Bars
      lags.forEach((lag, i) => {
        const corr = correlations[i];
        if (corr == null) return;

        const absCorr = Math.abs(corr);
        const isPeak = lag === pair.peakLag;
        const barColor = isPeak ? COLORS.peakHighlight : getLagColor(lag);

        const barX = x(String(lag));
        const barY = y(absCorr);
        const barH = innerH - barY;
        const barW = x.bandwidth();

        if (barW <= 0 || barH <= 0) return;

        g.append('rect')
          .attr('x', barX)
          .attr('y', barY)
          .attr('width', barW)
          .attr('height', barH)
          .attr('fill', barColor)
          .attr('rx', 2)
          .attr('ry', 2)
          .attr('opacity', isPeak ? 1 : 0.65)
          .attr('stroke', isPeak ? '#991b1b' : 'none')
          .attr('stroke-width', isPeak ? 1.5 : 0)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              from: pair.from,
              to: pair.to,
              lag,
              correlation: corr,
              isPeak,
              lagType: getLagLabel(lag),
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });

        // Peak label above bar
        if (isPeak) {
          g.append('text')
            .attr('x', barX + barW / 2)
            .attr('y', barY - 4)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('font-weight', 700)
            .attr('fill', '#991b1b')
            .text(absCorr.toFixed(2))
            .style('pointer-events', 'none');
        }
      });
    });

    return () => {
      pairs.forEach((_, idx) => {
        const svgEl = svgRefs.current[idx];
        if (svgEl) {
          d3.select(svgEl).selectAll('*').remove();
        }
      });
    };
  }, [pairs]);

  if (!data || !data.pairs || data.pairs.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Lead-Lag Indicators</div>
        <div style={styles.subtitle}>Which price fields move first?</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>Lead-Lag Indicators</div>
          <div style={styles.subtitle}>Which price fields move first?</div>
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: COLORS.leading }} />
          <span>Leading (neg lag)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: COLORS.synchronous }} />
          <span>Synchronous (lag 0)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: COLORS.lagging }} />
          <span>Lagging (pos lag)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: COLORS.peakHighlight }} />
          <span>Peak correlation</span>
        </div>
      </div>

      {/* Cross-correlograms for each pair */}
      <div style={styles.pairContainer}>
        {pairs.map((pair, idx) => (
          <div key={`${pair.from}-${pair.to}-${idx}`} style={styles.pairCard}>
            <div style={styles.pairLabel}>
              {formatPairLabel(pair.from, pair.to)}
              <span style={{
                marginLeft: '10px',
                fontSize: '11px',
                fontWeight: 500,
                color: getLagColor(pair.peakLag),
                background: pair.peakLag < 0 ? '#eff6ff' : pair.peakLag > 0 ? '#fff7ed' : '#f9fafb',
                padding: '2px 8px',
                borderRadius: '10px',
              }}>
                Peak at lag {pair.peakLag} (r={pair.peakCorrelation.toFixed(2)})
              </span>
            </div>
            <div style={styles.pairInterpretation}>{pair.interpretation}</div>
            <div style={{ overflowX: 'auto' }}>
              <svg
                ref={(el) => { svgRefs.current[idx] = el; }}
                style={{ width: '100%', height: 'auto', maxHeight: '160px' }}
                preserveAspectRatio="xMidYMid meet"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Summary section */}
      {significantPairs.length > 0 && (
        <div style={styles.summarySection}>
          <div style={styles.summaryTitle}>Significant Lead-Lag Relationships</div>
          <div style={styles.summaryList}>
            {significantPairs.map((pair, idx) => {
              const isLeading = pair.peakLag < 0;
              const absPeak = Math.abs(pair.peakLag);
              const dotColor = isLeading ? COLORS.leading : COLORS.lagging;
              const description = isLeading
                ? `${pair.from} leads ${pair.to} by ${absPeak} period${absPeak !== 1 ? 's' : ''}`
                : `${pair.to} leads ${pair.from} by ${absPeak} period${absPeak !== 1 ? 's' : ''}`;
              return (
                <div key={`summary-${idx}`} style={styles.summaryItem}>
                  <span style={{ ...styles.summaryDot, background: dotColor }} />
                  <span>{description}</span>
                  <span style={styles.summaryValue}>r={pair.peakCorrelation.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {significantPairs.length === 0 && (
        <div style={styles.summarySection}>
          <div style={styles.summaryTitle}>Significant Lead-Lag Relationships</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
            No significant lead-lag relationships detected (all pairs are synchronous or weakly correlated)
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatPairLabel(tooltip.from, tooltip.to)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Lag</span>
            <span style={{ fontWeight: 600 }}>{tooltip.lag}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Correlation</span>
            <span style={{ fontWeight: 600 }}>{tooltip.correlation.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Type</span>
            <span style={{ fontWeight: 600, color: getLagColor(tooltip.lag) }}>{tooltip.lagType}</span>
          </div>
          {tooltip.isPeak && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#991b1b', fontWeight: 600 }}>
              Peak correlation
            </div>
          )}
        </div>
      )}
    </div>
  );
}
